/* =============================================================================
 * Gigi Loop Bundle — turns a mixed box into a Loop subscription bundle
 * =============================================================================
 * Framework-free. Shared verbatim by:
 *   - the PDP buy box (sections/section-gigi-pdp-buy.liquid)
 *   - both quick-buy drawers (gigi-collection-grid, section-gigi-product-grid)
 *   - the offline test suite (tests/loop-bundle.mjs)
 * via window.GigiLoopBundle.
 *
 * WHY THIS EXISTS
 * Adding three flavour lines to the cart produces three *unlinked* subscription
 * lines: the customer's account page then shows three rows, each with its own
 * flavour dropdown and an unbounded quantity box, so a "3 tubs" contract can
 * silently drift to 2 or 4 (this has already happened in production).
 *
 * Loop groups the lines into ONE bundle if we (a) register the chosen mix as a
 * bundle *transaction* and (b) stamp the returned txnId onto every cart line as
 * the line-item property `_bundleId`. Loop then gives the subscriber a
 * pre-filled "Update bundle" picker in the portal that enforces the box size,
 * and applies the same structure to every renewal.
 *
 * VERIFIED AGAINST THE LIVE STORE (25 Aug 2026, API version 2026-04):
 *   POST /storefront/2026-04/bundle/{id}/transaction  ->  { data: { txnId } }
 *   then /cart/add.js with properties._bundleId on EVERY line.
 * The sell-side APIs are storefront-callable with NO token; the store is
 * identified by the `myshopifyDomain` query parameter.
 *
 * TWO RULES THAT WILL BITE IF BROKEN
 *  1. Top-level `quantity` in the transaction body is the number of BOXES and
 *     must stay 1. The per-flavour counts go in productVariants[].quantity.
 *     Loop's docs: passing the total item count there "will corrupt the
 *     subscription".
 *  2. The property key must be exactly `_bundleId`. Any other key and Loop
 *     treats the lines as ordinary, unbundled subscription lines. The leading
 *     underscore also hides it from the cart and checkout UI.
 *
 * UMD wrapper: the repo root is CommonJS, so this must resolve as CJS in Node
 * while exposing a browser global. Same pattern as gigi-mixer.js.
 * ===========================================================================*/
(function (root, factory) {
  var api = factory();
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = api; // Node (CommonJS) — `import LoopBundle from '...'`
  } else {
    root.GigiLoopBundle = api; // Browser global
  }
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  var API_BASE = 'https://api.loopsubscriptions.com/storefront/2026-04';

  /* ---------------------------------------------------------------------------
   * Pure helpers — no network, no DOM. These are what the offline suite covers.
   * ------------------------------------------------------------------------ */

  /**
   * Cart items (the mixer's own payload shape) -> Loop's productVariants array.
   * Ids are forced to Number: Loop rejects the string form quietly.
   */
  function toProductVariants(items) {
    var out = [];
    for (var i = 0; i < (items || []).length; i++) {
      var it = items[i];
      if (!it || !it.quantity) { continue; }
      out.push({ shopifyId: Number(it.id), quantity: Number(it.quantity) });
    }
    return out;
  }

  /** Total units across the cart items — the box size the customer built. */
  function totalUnits(items) {
    var n = 0;
    for (var i = 0; i < (items || []).length; i++) { n += Number((items[i] || {}).quantity) || 0; }
    return n;
  }

  /**
   * Choose the bundle's box size for a given unit count.
   * Loop expresses a fixed-size box as minSize = N with maxSize = 0, and a
   * ranged one as minSize..maxSize.
   *
   * ONLY a box that genuinely holds this many units is returned; there is no
   * "closest" or "default" fallback. Sending Loop a box size the selection does
   * not fit produces a transaction that disagrees with the cart, which is far
   * worse than falling back to an ordinary unbundled add. Returns null when
   * nothing fits — the caller must NOT invent one.
   */
  function pickBoxSizeId(boxSizes, units) {
    var list = boxSizes || [];
    for (var i = 0; i < list.length; i++) {
      var b = list[i];
      var min = Number(b.minSize) || 0;
      var max = Number(b.maxSize) || 0;
      var fits = max > 0 ? (units >= min && units <= max) : (units === min);
      if (fits) { return b.id; }
    }
    return null;
  }

  /** Is this selling plan actually linked to the bundle? */
  function hasSellingPlan(bundle, sellingPlanId) {
    var plans = (bundle || {}).sellingPlans || [];
    for (var i = 0; i < plans.length; i++) {
      if (Number(plans[i].shopifyId) === Number(sellingPlanId)) { return true; }
    }
    return false;
  }

  /**
   * Pick which selling plan to sell the box on: the first candidate the bundle
   * is ACTUALLY linked to wins. Blank/null candidates are skipped. Returns null
   * when the bundle carries none of them.
   *
   * WHY THIS IS NOT JUST `candidates[0]`
   * A Loop bundle inherits its product list from ONE selling plan group, and
   * that link is re-pointed in Loop's admin — instantly, for everyone, with no
   * deploy. If the theme insisted on a single hardcoded plan id, the moment
   * someone switched the bundle's group every mixed box in flight would fail
   * `hasSellingPlan` and land as loose unbundled lines until a matching theme
   * was published. Accepting an ordered candidate list makes the two changes
   * independent: the theme works against the old link and the new one, so the
   * admin switch can happen whenever, with no window where bundling is broken.
   */
  function resolveSellingPlanId(bundle, candidates) {
    var list = [].concat(candidates || []);
    for (var i = 0; i < list.length; i++) {
      var c = list[i];
      if (c === null || c === undefined || c === '') { continue; }
      if (hasSellingPlan(bundle, c)) { return Number(c); }
    }
    return null;
  }

  /**
   * Every variant the customer picked must exist in the bundle, or Loop will
   * reject the transaction. Returns the offending ids (empty array = all good).
   */
  function missingVariants(bundle, items) {
    var known = {};
    var products = (bundle || {}).products || [];
    for (var p = 0; p < products.length; p++) {
      var vs = products[p].variants || [];
      for (var v = 0; v < vs.length; v++) { known[String(vs[v].shopifyId)] = true; }
    }
    var missing = [];
    for (var i = 0; i < (items || []).length; i++) {
      if (!known[String(items[i].id)]) { missing.push(Number(items[i].id)); }
    }
    return missing;
  }

  /**
   * Stamp the transaction id onto every line as `_bundleId`, WITHOUT mutating
   * the caller's array (the mixer reuses its items) and preserving any
   * properties already present.
   */
  function stamp(items, txnId, bundleName, sellingPlanId) {
    return (items || []).map(function (it) {
      var props = {};
      var existing = it.properties || {};
      for (var k in existing) { if (Object.prototype.hasOwnProperty.call(existing, k)) { props[k] = existing[k]; } }
      props._bundleId = txnId;
      // Loop's own bundle page also sends `_bundleName` on every line (verified
      // by intercepting its /cart/add.js call). It is what the "Show bundle name
      // on checkout" preference surfaces, so the three lines read as one named
      // bundle rather than three unrelated purchases.
      if (bundleName) { props._bundleName = bundleName; }
      var copy = {};
      for (var j in it) { if (Object.prototype.hasOwnProperty.call(it, j)) { copy[j] = it[j]; } }
      copy.properties = props;
      // The cart lines must sit on the SAME plan the transaction was created
      // against, or Loop sees a bundle whose lines belong to a different plan
      // group and the contract comes out unbundled. Only rewritten when the
      // caller resolved a plan; otherwise the line keeps whatever it had.
      if (sellingPlanId !== null && sellingPlanId !== undefined && sellingPlanId !== '') {
        copy.selling_plan = Number(sellingPlanId);
      }
      return copy;
    });
  }

  /** Build the transaction request body. Box count is ALWAYS 1 — see rule 1. */
  function buildTransactionBody(opts) {
    return {
      boxSizeId: opts.boxSizeId,
      sellingPlanShopifyId: Number(opts.sellingPlanId),
      quantity: 1,
      productVariants: toProductVariants(opts.items),
    };
  }

  /* ---------------------------------------------------------------------------
   * Network. `fetchImpl` is injectable so the suite can drive these offline.
   * Loop wraps every response in { success, message, data, code } — we branch
   * on `code`/`success`, never on the human-readable message.
   * ------------------------------------------------------------------------ */

  function unwrap(json) {
    if (!json || json.success !== true) {
      var code = (json && json.code) || 'UNKNOWN';
      var err = new Error('Loop bundle API error: ' + code);
      err.code = code;
      throw err;
    }
    return json.data;
  }

  /** GET the bundle config: box sizes, linked selling plans, products/variants. */
  function readBundle(opts) {
    var f = opts.fetchImpl || fetch;
    var url = API_BASE + '/bundle/' + encodeURIComponent(opts.bundleId) +
      '?myshopifyDomain=' + encodeURIComponent(opts.domain);
    return f(url, { headers: { accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(unwrap);
  }

  /** POST the chosen mix, returning the txnId to stamp onto the cart lines. */
  function createTransaction(opts) {
    var f = opts.fetchImpl || fetch;
    var url = API_BASE + '/bundle/' + encodeURIComponent(opts.bundleId) +
      '/transaction?myshopifyDomain=' + encodeURIComponent(opts.domain);
    return f(url, {
      method: 'POST',
      headers: { accept: 'application/json', 'content-type': 'application/json' },
      body: JSON.stringify(buildTransactionBody(opts)),
    })
      .then(function (r) { return r.json(); })
      .then(unwrap)
      .then(function (data) {
        if (!data || !data.txnId) { throw new Error('Loop bundle API returned no txnId'); }
        return data.txnId;
      });
  }

  /**
   * The one call the buy box makes: given the mixer's cart items, return the
   * SAME items stamped with `_bundleId` and moved onto the plan the bundle is
   * linked to.
   *
   * Pass `sellingPlanIds` (ordered candidates — preferred first) or the single
   * `sellingPlanId`. The first candidate the bundle actually carries is used
   * for both the transaction and the cart lines; see resolveSellingPlanId for
   * why that indirection exists.
   *
   * Resolves to the untouched items when the bundle cannot be used (no bundle
   * configured, plan not linked, unknown variant, no matching box size, or the
   * API failed). Rationale: an unbundled subscription is exactly today's
   * behaviour — correct, fulfillable, just without the portal picker — so a
   * Loop outage must never block the sale. `onFallback` is called with the
   * reason so the caller can log it.
   */
  function attach(opts) {
    var items = opts.items || [];
    var note = opts.onFallback || function () {};
    if (!opts.bundleId || !opts.domain) { note('no-bundle-configured'); return Promise.resolve(items); }
    var candidates = opts.sellingPlanIds || [opts.sellingPlanId];

    var bundleName = null;
    var planId = null;
    return readBundle(opts)
      .then(function (bundle) {
        bundleName = opts.bundleName || bundle.name || null;
        planId = resolveSellingPlanId(bundle, candidates);
        if (planId === null) { throw new Error('plan-not-linked'); }
        var missing = missingVariants(bundle, items);
        if (missing.length) { throw new Error('variant-not-in-bundle:' + missing.join(',')); }
        var boxSizeId = pickBoxSizeId(bundle.boxSizes, totalUnits(items));
        if (!boxSizeId) { throw new Error('no-matching-box-size'); }
        return createTransaction({
          fetchImpl: opts.fetchImpl,
          domain: opts.domain,
          bundleId: opts.bundleId,
          boxSizeId: boxSizeId,
          sellingPlanId: planId,
          items: items,
        });
      })
      .then(function (txnId) { return stamp(items, txnId, bundleName, planId); })
      .catch(function (e) { note(e && e.message ? e.message : String(e)); return items; });
  }

  return {
    API_BASE: API_BASE,
    toProductVariants: toProductVariants,
    totalUnits: totalUnits,
    pickBoxSizeId: pickBoxSizeId,
    hasSellingPlan: hasSellingPlan,
    resolveSellingPlanId: resolveSellingPlanId,
    missingVariants: missingVariants,
    stamp: stamp,
    buildTransactionBody: buildTransactionBody,
    readBundle: readBundle,
    createTransaction: createTransaction,
    attach: attach,
  };
});
